/**
 * Sentry tag keys and error-type values for the frontend.
 *
 * Centralized so the same strings are reused across the error helpers
 * (src/lib/sentry.ts), the React Query error handler, and the route-level
 * error boundary (No Loose Strings policy). The `type` tag values are how
 * issues are grouped and filtered in Sentry.
 */

/** Sentry tag keys used across the frontend. */
export const SENTRY_TAGS = {
  TYPE: "type",
  FLOW: "flow",
  ACTION: "action",
  MODEL: "model",
  ENDPOINT: "endpoint",
  SOURCE: "source",
  ROUTE: "route",
  /** Bot id — present on bot-builder + bot-chat captures. */
  BOT_ID: "bot_id",
  /** Bot surface: "builder" (owner CRUD) | "chat" (visitor/preview stream). */
  SURFACE: "surface",
} as const;

/** Values for the Sentry `type` tag. */
export const SENTRY_ERROR_TYPES = {
  PAYMENT_FAILURE: "payment_failure",
  GENERATION_FAILURE: "generation_failure",
  QUERY_FAILURE: "query_failure",
  ROUTE_ERROR: "route_error",
  SSE_PARSE_ERROR: "sse_parse_error",
  /** AuthContext.fetchProfile swallowed a Supabase error. */
  PROFILE_FETCH_FAILURE: "profile_fetch_failure",
  /** /api/auth/track-signup POST failed — silent loss of referral attribution. */
  TRACK_SIGNUP_FAILURE: "track_signup_failure",
  /** Bot-builder (owner CRUD) frontend error. */
  BOT_BUILDER_FAILURE: "bot_builder_failure",
  /** Bot chat (preview/embed) frontend error — stream, parse, transport. */
  BOT_CHAT_FAILURE: "bot_chat_failure",
} as const;

/**
 * Session Replay sampling. Both 0 — Replay never records automatically.
 * It is started manually via `setReplayEnabled` (src/lib/sentryReplay.ts)
 * only after the user grants analytics cookie consent.
 */
export const REPLAY_SESSION_SAMPLE_RATE = 0;
export const REPLAY_ON_ERROR_SAMPLE_RATE = 0;

/**
 * Error-message patterns dropped before an event ships to Sentry
 * (Sentry.init `ignoreErrors` in src/main.tsx). Centralized here — same
 * reasoning as SENTRY_PII_SCRUB below — so the list isn't a loose-string
 * literal inside the init block and can be unit-tested against the real
 * captured strings (see src/config/sentry.test.ts).
 *
 * Sentry matches each pattern against the event's error message
 * (`type: value`), so the regexes target the message text, not stack frames.
 *
 * Buckets:
 * - User-cancel / transport aborts the user already saw → not actionable.
 * - Browser-extension & in-app-browser WebView noise that isn't our code.
 * - External injected scripts (CSP EvalError + Safari push from the Facebook
 *   in-app browser; the Meta IAB "unified bridge" reaching for an absent
 *   `window.webkit.messageHandlers` — TODOWITH-AI-FRONTEND-1N).
 * - Stale-chunk load failures after a deploy, now auto-recovered by a single
 *   reload in src/lib/lazyRoute.ts.
 */
export const SENTRY_IGNORE_ERRORS: readonly RegExp[] = [
  /AbortError/i,
  /The operation was aborted/i,
  /The play\(\) request was interrupted/i,
  /Share canceled/i,
  /Not authenticated/i,
  // Browser extensions & in-app browser WebView noise
  /Can't find variable: EmptyRanges/i,
  /Java object is gone/i,
  /postMessage/i,
  /ResizeObserver loop/i,
  // External-injected noise that isn't our code: CSP EvalError from the
  // Facebook in-app browser, Safari push API access on platforms that don't
  // expose it, and the Meta in-app-browser "unified bridge" (sendDataToNative)
  // reaching for window.webkit.messageHandlers when the WKWebView handler is
  // absent. None of these symbols exist in our codebase — Sentry mislabels
  // them in-app because the synthetic frame filename is our route URL.
  /unsafe-eval/i,
  /safari\.pushNotification/i,
  /window\.webkit\.messageHandlers/i,
  // Stale-chunk load failures after a deploy — now auto-recovered by a
  // single full reload in src/lib/lazyRoute.ts, so they're not actionable.
  // Keep these in sync with CHUNK_LOAD_ERROR there (the Safari MIME-type
  // variant is TODOWITH-AI-FRONTEND-1J).
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /not a valid JavaScript MIME type/i,
  /Failed to load module script/i,
];

/**
 * PII scrubbing constants used by Sentry.init's beforeSend / beforeBreadcrumb
 * (src/main.tsx). Centralized here so the security-critical allowlist isn't a
 * loose-string literal inside the init block. Adding a new sensitive query key
 * or credential shape happens here, not in main.tsx.
 *
 * - SENSITIVE_QUERY_KEYS: query-string keys whose VALUE is redacted in any URL
 *   we ship to Sentry (PayPlus page_request_uid, OAuth tokens, magic-link codes,
 *   the user's email when it appears as a query parameter, generic api_key /
 *   bearer / jwt names).
 * - SENSITIVE_HASH_RE: matches URL fragments (`#...`) that carry credentials
 *   in Supabase's recovery / OAuth implicit-grant flows (`#access_token=...`).
 */
export const SENTRY_PII_SCRUB = {
  SENSITIVE_QUERY_KEYS: [
    "page_request_uid",
    "token",
    "access_token",
    "refresh_token",
    "code",
    "id_token",
    "session",
    "email",
    "secret",
    "api_key",
    "bearer",
    "jwt",
    "authorization",
  ] as readonly string[],
  // OP-5: covers bearer / jwt / authorization / api_key / sk_live / sk_test.
  SENSITIVE_HASH_RE:
    /token|secret|code|api[_-]?key|bearer|jwt|authorization|sk_(live|test)/i,
} as const;

/**
 * Scrub a URL for safe shipping to Sentry. Used by Sentry.init's beforeSend
 * (request.url) and beforeBreadcrumb (data.url/to/from). Returns the input
 * unchanged when no scrub is needed; returns "[redacted-mailto]" for mailto:.
 *
 * Never throws — callers also wrap in try/catch so a scrub bug can't drop the
 * original event entirely.
 */
export function scrubUrlForSentry(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  try {
    const u = new URL(raw, "http://placeholder.invalid");
    // OP-6: mailto: URLs always contain a user email — redact wholesale.
    if (u.protocol === "mailto:") return "[redacted-mailto]";
    let mutated = false;
    for (const key of SENTRY_PII_SCRUB.SENSITIVE_QUERY_KEYS) {
      if (u.searchParams.has(key)) {
        u.searchParams.set(key, "[REDACTED]");
        mutated = true;
      }
    }
    if (u.hash && SENTRY_PII_SCRUB.SENSITIVE_HASH_RE.test(u.hash)) {
      u.hash = "[REDACTED]";
      mutated = true;
    }
    // Drop URL userinfo (https://user:pass@host/...) wholesale.
    if (u.username || u.password) {
      u.username = "";
      u.password = "";
      mutated = true;
    }
    return mutated ? `${u.pathname}${u.search}${u.hash}` : raw;
  } catch {
    return raw;
  }
}
